/**
 * JANIMONY.CHAT - AGENT API SERVICE
 * Handles all backend communication for agents
 */

import axios from 'axios';

const API_URL = 'https://janimony-backend.onrender.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Handle responses
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error.response?.data || error.message);
  }
);

const API = {
  /**
   * Get active chat sessions for a business
   */
  getActiveChats: async (businessId) => {
    try {
      return await api.get(`/chat/active/${businessId}`);
    } catch (error) {
      // Demo fallback
      return {
        success: true,
        sessions: [
          {
            id: 'session_1',
            session_id: 'sess_demo_12345678',
            business_id: businessId,
            status: 'waiting',
            started_at: new Date(Date.now() - 300000).toISOString(), // 5 min ago
            messages: [
              { content: 'Hi, I need help with my order', sender_type: 'customer' }
            ]
          },
          {
            id: 'session_2',
            session_id: 'sess_demo_87654321',
            business_id: businessId,
            status: 'active',
            started_at: new Date(Date.now() - 600000).toISOString(), // 10 min ago
            messages: [
              { content: 'How do I reset my password?', sender_type: 'customer' }
            ]
          }
        ]
      };
    }
  },

  /**
   * Get messages for a specific chat session
   */
  getChatMessages: async (businessId, sessionId) => {
    try {
      return await api.get(`/chat/messages/${businessId}/${sessionId}`);
    } catch (error) {
      // Demo fallback
      return {
        success: true,
        messages: [
          {
            id: '1',
            sender_type: 'customer',
            content: 'Hello! I need help with something',
            created_at: new Date(Date.now() - 300000).toISOString()
          },
          {
            id: '2',
            sender_type: 'ai',
            content: 'Hi! I\'m here to help. What do you need assistance with?',
            created_at: new Date(Date.now() - 280000).toISOString()
          },
          {
            id: '3',
            sender_type: 'customer',
            content: 'I want to talk to a human agent please',
            created_at: new Date(Date.now() - 260000).toISOString()
          }
        ],
        customerTyping: false,
        agentConnected: true
      };
    }
  },

  /**
   * Send agent reply
   */
  sendAgentReply: async (sessionId, agentId, message) => {
    try {
      return await api.post('/chat/agent-reply', {
        sessionId,
        agentId,
        message
      });
    } catch (error) {
      // Demo fallback
      return {
        success: true,
        message: 'Reply sent (demo mode)'
      };
    }
  },

  /**
   * Close a chat session
   */
  closeSession: async (sessionId) => {
    try {
      return await api.post(`/chat/close/${sessionId}`);
    } catch (error) {
      // Demo fallback
      return {
        success: true,
        message: 'Session closed (demo mode)'
      };
    }
  },

  /**
   * Update typing status
   */
  updateTyping: async (sessionId, isTyping) => {
    try {
      return await api.post('/chat/typing', {
        sessionId,
        isTyping,
        userType: 'agent'
      });
    } catch (error) {
      return { success: false };
    }
  }
};

export default API;